import { useMemo } from 'react';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Button,
  Spinner,
} from '@fluentui/react-components';
import { CircleCheck, Download, ExternalLink, RefreshCw, TriangleAlert } from 'lucide-react';

import type { SignInHelperLinks } from '../config/consoleConfig';
import { getSignInHelperLinks } from '../config/signInHelperLinks';
import type { SignInHelperState } from '../hooks/useSignInHelper';
import { DetectedBrowser, detectBrowser } from '../lib/browserDetection';
import { SIGN_IN_PROTOCOL } from '../lib/signInHelper';
import { ACCENT, RADIUS, STATUS, STROKE, SURFACE, TEXT } from '../theme/tokens';

import AirBridgeMark from './AirBridgeMark';
import BrandLogo from './BrandLogo';
import { ManualInstallSteps } from './SignInHelperInstall';

// FD-612 — the AirBridge Sign-in Helper, as one card in the activation dialog.
//
// EVERY STATE COMES FROM THE EXISTING DETECTION (FD-604/607/608): the relay ping in
// `useSignInHelper`, its latched `everDetected`, and the pong's protocol. Nothing new
// is detected here. In particular the card never says "installed" unless a ping was
// answered — a helper we could not hear from is "not detected" or "cannot be
// verified", never assumed present.
//
// The browser (Edge / Chrome / neither) comes from `detectBrowser`. An unsupported
// browser is said to be unsupported: no logo for a browser we did not detect, and no
// Download button for an extension it cannot run.
//
// ADVISORY. Nothing here gates the primary button (FD-296). Without the helper the
// browser sign-in still works: the operator pastes one address back.

export type HelperCardState = 'checking' | 'installed' | 'not-detected' | 'cannot-verify' | 'unsupported';

/**
 * The card's state, from the existing detection signals only.
 *
 * - A ping answered on this page with a protocol we speak → `installed`.
 * - A ping answered with a protocol we do not speak → `cannot-verify`.
 * - No answer now, but an answer earlier on this page (FD-607: a relay orphaned by an
 *   extension update or reload) → `cannot-verify`. Reloading the tab fixes it.
 * - No answer ever, in a browser the helper does not support → `unsupported`.
 * - No answer ever, in Edge or Chrome → `not-detected`.
 */
export function helperCardState(
  helper: Pick<SignInHelperState, 'status' | 'pong' | 'everDetected'>,
  browser: DetectedBrowser,
): HelperCardState {
  if (helper.status === 'checking') return 'checking';
  if (helper.status === 'present') {
    const protocol = helper.pong?.protocol;
    if (typeof protocol === 'number' && protocol !== SIGN_IN_PROTOCOL) return 'cannot-verify';
    return 'installed';
  }
  if (helper.everDetected) return 'cannot-verify';
  if (browser.kind === 'unsupported') return 'unsupported';
  return 'not-detected';
}

/** The store listing for THIS browser, when configured. */
function storeUrlFor(browser: DetectedBrowser, links: SignInHelperLinks | undefined): string | undefined {
  if (browser.kind === 'edge') return links?.edgeStoreUrl;
  if (browser.kind === 'chrome') return links?.chromeStoreUrl;
  return undefined;
}

export interface SignInHelperCardProps {
  helper: SignInHelperState;
  /** Injectable for tests; defaults to the running browser. */
  browser?: DetectedBrowser;
  /** Injectable for tests; defaults to the console config's links. */
  links?: SignInHelperLinks;
}

export default function SignInHelperCard({ helper, browser: browserProp, links: linksProp }: SignInHelperCardProps) {
  const browser = useMemo(() => browserProp ?? detectBrowser(), [browserProp]);
  const links = linksProp ?? getSignInHelperLinks();
  const state = helperCardState(helper, browser);
  const supported = browser.kind !== 'unsupported';
  const title = supported ? `AirBridge Sign-in Helper for ${browser.name}` : 'AirBridge Sign-in Helper';
  const storeUrl = storeUrlFor(browser, links);

  const logos = (
    <span className="flex shrink-0 items-center gap-2.5" aria-hidden="true">
      <AirBridgeMark size={state === 'installed' ? 18 : 24} />
      {supported && (
        <>
          <span style={{ width: 1, alignSelf: 'stretch', backgroundColor: STROKE.base }} />
          <BrandLogo brand={browser.kind === 'edge' ? 'edge' : 'chrome'} size={state === 'installed' ? 20 : 26} />
        </>
      )}
    </span>
  );

  // ── Installed: one compact row ───────────────────────────────────────────
  if (state === 'installed') {
    return (
      <div
        className="flex items-center gap-3 px-3 py-2.5"
        style={{ border: `1px solid ${STROKE.base}`, borderRadius: RADIUS.lg, backgroundColor: SURFACE.base }}
        data-testid="sign-in-helper-card"
        data-state={state}
      >
        {logos}
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-semibold" style={{ color: TEXT.primary }}>
            {title}
          </div>
          <div className="flex items-center gap-1 text-[12px]" style={{ color: STATUS.successText }}>
            <CircleCheck size={14} strokeWidth={1.5} aria-hidden="true" />
            Installed and ready
          </div>
        </div>
        {/* Manage only where there is somewhere to manage it: this browser's store listing. */}
        {storeUrl && (
          <Button
            as="a"
            appearance="secondary"
            size="small"
            href={storeUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Manage the AirBridge Sign-in Helper in the ${browser.name} store`}
          >
            Manage
          </Button>
        )}
      </div>
    );
  }

  // ── Every other state: the prominent card ───────────────────────────────
  const recheck = (
    <Button
      appearance="transparent"
      size="small"
      icon={<RefreshCw size={14} strokeWidth={1.5} />}
      onClick={helper.recheck}
      style={{ color: ACCENT.pressed }}
    >
      Check again
    </Button>
  );

  return (
    <div
      className="flex flex-col gap-2.5 px-4 py-3.5"
      style={{ border: `1px solid ${STROKE.base}`, borderRadius: RADIUS.lg, backgroundColor: ACCENT.tint }}
      data-testid="sign-in-helper-card"
      data-state={state}
    >
      {logos}
      <div className="flex flex-col gap-0.5">
        <div className="text-[14px] font-semibold" style={{ color: TEXT.primary }}>
          {title}
        </div>
        <div className="text-[12.5px] leading-snug" style={{ color: TEXT.secondary }}>
          The helper brings the Microsoft sign-in back to this page, so the console continues by itself.
        </div>
      </div>

      <StatusLine state={state} browser={browser} />

      {state === 'not-detected' && <InstallActions storeUrl={storeUrl} browser={browser} links={links} />}

      {state !== 'checking' && state !== 'unsupported' && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-[12px]" style={{ color: TEXT.secondary }}>
            {state === 'cannot-verify'
              ? 'Reload this tab, then check again.'
              : 'After installation, click “Check again” to verify.'}
          </span>
          {recheck}
        </div>
      )}
    </div>
  );
}

function StatusLine({ state, browser }: { state: HelperCardState; browser: DetectedBrowser }) {
  if (state === 'checking') {
    return (
      <div className="flex items-center gap-2 text-[12.5px]" style={{ color: TEXT.secondary }} data-testid="helper-status">
        <Spinner size="extra-tiny" />
        Looking for the helper in this browser…
      </div>
    );
  }
  if (state === 'unsupported') {
    return (
      <div className="text-[12.5px] leading-snug" style={{ color: TEXT.primary }} data-testid="helper-status">
        The helper is available for Microsoft Edge and Google Chrome
        {browser.name ? `, not ${browser.name}` : ''}. You can still sign in from this browser: paste the
        address the sign-in tab lands on, or choose another method under Advanced sign-in options.
      </div>
    );
  }
  const text =
    state === 'cannot-verify'
      ? 'Helper cannot be verified. It answered on this page before but is not responding now.'
      : 'Helper not detected';
  return (
    <div
      className="flex items-start gap-1.5 text-[13px] font-semibold"
      style={{ color: STATUS.warningText }}
      data-testid="helper-status"
    >
      {/* The warning GLYPH hex fails on white; the label rung carries both. */}
      <TriangleAlert size={16} strokeWidth={1.5} aria-hidden="true" style={{ marginTop: 1, flexShrink: 0 }} />
      {text}
    </div>
  );
}

/**
 * The install route, from the same config rules as the Activate page (FD-610):
 * this browser's store listing first, the self-hosted download second, and "from
 * your administrator" when nothing is configured — never a link that goes nowhere.
 */
function InstallActions({
  storeUrl,
  browser,
  links,
}: {
  storeUrl: string | undefined;
  browser: DetectedBrowser;
  links: SignInHelperLinks | undefined;
}) {
  const downloadUrl = links?.downloadUrl;
  const primaryHref = storeUrl ?? downloadUrl;

  if (!primaryHref) {
    return (
      <div className="text-[12.5px]" style={{ color: TEXT.secondary }} data-testid="helper-from-admin">
        The AirBridge sign-in helper is available from your administrator.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          as="a"
          appearance="primary"
          href={primaryHref}
          icon={<Download size={16} strokeWidth={1.5} />}
          {...(storeUrl
            ? { target: '_blank', rel: 'noopener noreferrer' }
            : { download: true })}
          data-testid="helper-download"
        >
          Download AirBridge Helper
        </Button>
        {storeUrl && downloadUrl && (
          <a
            href={downloadUrl}
            download
            className="inline-flex items-center gap-1 text-[12px] underline"
            style={{ color: ACCENT.pressed }}
            data-testid="helper-download-interim"
          >
            Or download the interim build
          </a>
        )}
        {storeUrl && (
          <span className="inline-flex items-center gap-1 text-[12px]" style={{ color: TEXT.secondary }}>
            Opens the {browser.name} store <ExternalLink size={12} strokeWidth={1.5} aria-hidden="true" />
          </span>
        )}
      </div>
      {downloadUrl && (
        <Accordion collapsible>
          <AccordionItem value="manual">
            <AccordionHeader size="small">Install the downloaded build</AccordionHeader>
            <AccordionPanel>
              <ManualInstallSteps />
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      )}
    </div>
  );
}
